import { useEffect, useState } from 'react';
import { usePublishedViewAuth } from './PublishedViewAuthContext';
import type {
  FetchResult,
  HttpErrorStatus,
  PublishedTimelineResponse,
  UsePublishedTimelineResult,
} from './types';
import { asHttpErrorStatus } from './types';

const HTTP_BAD_REQUEST = 400 as HttpErrorStatus;
const HTTP_UNAUTHORIZED = 401 as HttpErrorStatus;
const HTTP_FORBIDDEN = 403 as HttpErrorStatus;
const HTTP_NOT_FOUND = 404 as HttpErrorStatus;

async function fetchJson<T>(
  url: string,
  label: string,
  headers?: HeadersInit,
): Promise<FetchResult<T>> {
  try {
    const response = await fetch(url, { headers });
    if (!response.ok) {
      const status = asHttpErrorStatus(response.status);
      if (status === null) {
        return {
          type: 'failure',
          error: new Error(`Unexpected HTTP status ${response.status} fetching ${label}`),
        };
      }
      return {
        type: 'http_error',
        status,
        error: new Error(`Failed to fetch ${label}: ${response.status} ${response.statusText}`),
      };
    }
    const data = await response.json();
    return { type: 'success', data };
  } catch (e) {
    return {
      type: 'failure',
      error: e instanceof Error ? e : new Error(`Network error fetching ${label}`),
    };
  }
}

async function fetchPublishedTimelineData(
  linkId: string,
  password?: string,
): Promise<FetchResult<PublishedTimelineResponse>> {
  const headers: HeadersInit = {};
  if (password) {
    headers['X-Published-View-Password'] = password;
  }

  const result = await fetchJson<PublishedTimelineResponse>(
    `/published/v1/roadmap/visualization/${linkId}`,
    'published timeline',
    headers,
  );

  if (result.type !== 'success') {
    return result;
  }

  if (!result.data.layout) {
    return {
      type: 'failure',
      error: new Error('Layout configuration is required in published timeline response'),
    };
  }

  return result;
}

function toHookResult(
  fetchResult: FetchResult<PublishedTimelineResponse>,
): UsePublishedTimelineResult {
  switch (fetchResult.type) {
    case 'success':
      return { status: 'success', data: fetchResult.data };

    case 'http_error':
      if (fetchResult.status === HTTP_UNAUTHORIZED) {
        return { status: 'password_required' };
      }
      if (fetchResult.status === HTTP_FORBIDDEN) {
        return { status: 'password_invalid' };
      }
      if (fetchResult.status === HTTP_BAD_REQUEST || fetchResult.status === HTTP_NOT_FOUND) {
        return { status: 'not_found' };
      }
      return { status: 'error', error: fetchResult.error };

    case 'failure':
      return { status: 'error', error: fetchResult.error };
  }
}

export function usePublishedTimeline(linkId: string): UsePublishedTimelineResult {
  const { submission } = usePublishedViewAuth();
  const [result, setResult] = useState<UsePublishedTimelineResult>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    setResult({ status: 'loading' });

    fetchPublishedTimelineData(linkId, submission?.password).then((fetchResult) => {
      if (!cancelled) {
        setResult(toHookResult(fetchResult));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [linkId, submission]);

  return result;
}
